/* ===== File Upload & Compression ===== */
import { storage } from '../config/firebase.js?v=83.58';
import { showUploadProgress } from '../utils/ui.js?v=83.58';
import { logger } from '../utils/logger.js?v=83.58';
import { withTimeoutMs } from '../utils/with-timeout.js?v=83.58';

const FOLDER_PROFILES = {
  backgrounds:     { maxWidth: 1920, quality: 0.75 },
  prizes:          { maxWidth: 800,  quality: 0.80 },
  prizes_default:  { maxWidth: 800,  quality: 0.80 },
  'client-promo':  { maxWidth: 1200, quality: 0.75 },
  confirm_bg:      { maxWidth: 1200, quality: 0.75 },
  qr:              { maxWidth: 600,  quality: 0.82 },
  'menu-qr':       { maxWidth: 600,  quality: 0.82 },
  'menu-gallery':  { maxWidth: 1600, quality: 0.78 },
  'feedback-qr':   { maxWidth: 600,  quality: 0.82 },
  'location-hours': { maxWidth: 1600, quality: 0.78 },
  'location-logo':  { maxWidth: 800,  quality: 0.85 },
  'event-gallery':  { maxWidth: 1200, quality: 0.75 },
  'loyalty-promo':  { maxWidth: 1200, quality: 0.75 },
  'joy-assistant':  { maxWidth: 1600, quality: 0.78 },
};
const DEFAULT_PROFILE = { maxWidth: 1280, quality: 0.78 };
/** Limită pe blob-ul comprimat urcat în Storage. */
export const MAX_COMPRESSED_UPLOAD_BYTES = 5 * 1024 * 1024;
/** Dacă compresia eșuează, acceptăm original doar până la această mărime. */
export const MAX_ORIGINAL_FALLBACK_BYTES = 40 * 1024 * 1024;
const MAX_SIZE = MAX_COMPRESSED_UPLOAD_BYTES;
const MAX_ORIGINAL_FALLBACK = MAX_ORIGINAL_FALLBACK_BYTES;

function isCoarsePointerDevice() {
  try {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(pointer: coarse)').matches
    );
  } catch (_) {
    return false;
  }
}
const COMPRESS_TIMEOUT_MS = 30000;
const UPLOAD_TIMEOUT_MS = 90000;

export function updateUploadProgress(pct, text) {
  showUploadProgress(pct, text);
}

function supportsWebP() {
  try {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    return c.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch (_) {
    return false;
  }
}

function loadImageFromObjectUrl(objectUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err || new Error('Nu s-a putut încărca imaginea.'));
    img.src = objectUrl;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('Compresia imaginii a durat prea mult.'));
    }, COMPRESS_TIMEOUT_MS);

    canvas.toBlob(
      (blob) => {
        window.clearTimeout(timer);
        if (blob) resolve(blob);
        else reject(new Error('Compresia imaginii a eșuat.'));
      },
      mime,
      quality
    );
  });
}

/**
 * Pozele de fundal sunt des capturi de ecran (o poză de pe Instagram/Facebook cu benzi negre sau albe sus și jos,
 * plus bara telefonului). Pe ecranul clientului se vedea poza sus și negru dedesubt. Găsim blocul cu poza propriu-zisă
 * (cel mai lung șir de rânduri „pline”) și tăiem restul. Întoarce { sx, sy, sw, sh } sau null dacă nu e nimic de tăiat.
 */
export function findPhotoCrop(img) {
  try {
    const W = img.naturalWidth || img.width;
    const H = img.naturalHeight || img.height;
    if (!W || !H) return null;
    const cw = 160;
    const ch = Math.max(8, Math.round((H / W) * cw));
    const c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    const x = c.getContext('2d', { willReadFrequently: true });
    if (!x) return null;
    x.drawImage(img, 0, 0, cw, ch);
    const d = x.getImageData(0, 0, cw, ch).data;
    const px = (i) => [d[i], d[i + 1], d[i + 2]];
    // culoarea benzilor = media celor 4 colțuri
    const cs = [0, (cw - 1) * 4, (ch - 1) * cw * 4, ((ch - 1) * cw + cw - 1) * 4].map(px);
    const bg = [0, 1, 2].map((k) => cs.reduce((a, v) => a + v[k], 0) / 4);
    const far = (i) => Math.abs(d[i] - bg[0]) + Math.abs(d[i + 1] - bg[1]) + Math.abs(d[i + 2] - bg[2]) > 42;
    const rowFull = (y) => { let n = 0; for (let xx = 0; xx < cw; xx++) if (far((y * cw + xx) * 4)) n++; return n / cw >= 0.3; };
    const rows = Array.from({ length: ch }, (_, y) => rowFull(y));
    // cel mai lung bloc de rânduri pline (golurile mici din poză nu-l rup)
    const gap = Math.max(2, Math.round(ch * 0.02));
    let best = [0, -1]; let s0 = -1; let last = -1;
    for (let y = 0; y < ch; y++) {
      if (!rows[y]) continue;
      if (s0 < 0 || y - last > gap) s0 = y;
      last = y;
      if (last - s0 > best[1] - best[0]) best = [s0, last];
    }
    const [y0, y1] = best;
    const bh = y1 - y0 + 1;
    if (y1 < 0 || bh < ch * 0.12) return null;
    // coloanele, doar în blocul găsit
    const colFull = (xx) => { let n = 0; for (let y = y0; y <= y1; y++) if (far((y * cw + xx) * 4)) n++; return n / bh >= 0.3; };
    let x0 = 0; while (x0 < cw - 1 && !colFull(x0)) x0++;
    let x1 = cw - 1; while (x1 > x0 && !colFull(x1)) x1--;
    const bw = x1 - x0 + 1;
    if (bw < cw * 0.4 || bh < bw * 0.3) return null; // nu o fâșie subțire (ex. bara telefonului)
    // tăiem doar dacă se câștigă ceva vizibil
    if (bh >= ch * 0.95 && bw >= cw * 0.95) return null;
    const k = W / cw;
    const pad = 1; // un rând de siguranță înăuntru, ca să nu rămână o linie de bandă
    const sx = Math.round((x0 + (x0 > 0 ? pad : 0)) * k);
    const sy = Math.round((y0 + (y0 > 0 ? pad : 0)) * k);
    const ex = Math.round((x1 + 1 - (x1 < cw - 1 ? pad : 0)) * k);
    const ey = Math.round((y1 + 1 - (y1 < ch - 1 ? pad : 0)) * k);
    return { sx, sy, sw: Math.max(1, Math.min(W, ex) - sx), sh: Math.max(1, Math.min(H, ey) - sy) };
  } catch (_) {
    return null;
  }
}

async function compressImage(file, folder) {
  const profile = FOLDER_PROFILES[folder] || DEFAULT_PROFILE;
  const maxW = isCoarsePointerDevice()
    ? Math.min(profile.maxWidth, 1400)
    : profile.maxWidth;
  const q = profile.quality;
  const objectUrl = URL.createObjectURL(file);

  try {
    const img = await withTimeoutMs(loadImageFromObjectUrl(objectUrl), COMPRESS_TIMEOUT_MS);

    const canvas = document.createElement('canvas');
    const crop = folder === 'backgrounds' ? findPhotoCrop(img) : null;
    let width = crop ? crop.sw : (img.naturalWidth || img.width);
    let height = crop ? crop.sh : (img.naturalHeight || img.height);

    if (!width || !height) {
      throw new Error('Dimensiunile imaginii nu pot fi citite.');
    }

    if (width > maxW) {
      height *= maxW / width;
      width = maxW;
    }

    canvas.width = Math.round(width);
    canvas.height = Math.round(height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas indisponibil pe acest dispozitiv.');
    }
    if (crop) ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, canvas.width, canvas.height);
    else ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const useWebP = supportsWebP();
    const mime = useWebP ? 'image/webp' : 'image/jpeg';
    const ext = useWebP ? '.webp' : '.jpg';
    const blob = await canvasToBlob(canvas, mime, q);
    return { blob, ext, mime };
  } finally {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch (_) {
      /* ignore */
    }
  }
}

export async function uploadFile(uid, folder, file) {
  if (!file) return null;

  console.log('IMAGE SELECTED', file.name, file.size, file.type);

  let fileToUpload = file;

  const looksLikeImage =
    /^image\//.test(file.type || '') ||
    /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name || '') ||
    (!file.type && file.size > 0);

  if (looksLikeImage) {
    try {
      const { blob, ext, mime } = await compressImage(file, folder);
      const baseName = (file.name || 'image').replace(/\.[^/.]+$/, '');
      fileToUpload = new File([blob], baseName + ext, { type: mime });
    } catch (e) {
      logger.warn('Compression failed.', e);
      if (file.size > MAX_ORIGINAL_FALLBACK) {
        const maxMb = (MAX_ORIGINAL_FALLBACK / (1024 * 1024)).toFixed(0);
        throw new Error(
          `Imaginea este prea mare (max ${maxMb}MB). Încearcă o poză mai mică sau alt format (JPG).`
        );
      }
      console.warn('UPLOAD FALLBACK original (compression failed)', e?.message);
    }
  }

  if (fileToUpload.size > MAX_SIZE) {
    const maxMb = (MAX_SIZE / (1024 * 1024)).toFixed(0);
    throw new Error(`Imaginea comprimată este încă prea mare (max ${maxMb}MB). Încearcă altă poză.`);
  }

  const originalName = fileToUpload.name || 'img.jpg';
  const safeName = originalName.replace(/[^\w.\-]+/g, '_');
  const path = `${uid}/${folder}/${Date.now()}_${safeName}`;

  const ref = storage.ref(path);
  const contentType =
    /^image\/(jpeg|jpg|png|gif|webp|avif|heic|heif)$/.test(String(fileToUpload.type || ''))
      ? fileToUpload.type
      : safeName.match(/\.webp$/i)
        ? 'image/webp'
        : safeName.match(/\.png$/i)
          ? 'image/png'
          : 'image/jpeg';

  console.log('UPLOAD START', path, fileToUpload.size);
  const task = ref.put(fileToUpload, { contentType });

  await withTimeoutMs(
    new Promise((res, rej) => {
      task.on(
        'state_changed',
        (s) => {
          const pct = (s.bytesTransferred / Math.max(s.totalBytes, 1)) * 100;
          updateUploadProgress(pct);
        },
        rej,
        res
      );
    }),
    UPLOAD_TIMEOUT_MS
  );

  const url = await withTimeoutMs(ref.getDownloadURL(), 15000);
  console.log('UPLOAD DONE', url);
  updateUploadProgress(100, 'Gata.');
  return url;
}
